import { ComboBoxContainer, ComboBoxContent, ComboBoxInner, ComboBoxText, ComboBoxTextPrefix } from "./styled";
import React from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import cx from "../../../../utils/cx";
import { useOnClickOutside } from "../../../../hooks/useOnClickOutside";
import Scrollbar from "../Scrollbar";
import Input from "../../newComponents/Input";

export interface IComboBoxItem {
    key: string;
    text: string;
    renderItem?: (item: IComboBoxItem) => JSX.Element;
    renderItemText?: (item: IComboBoxItem) => JSX.Element;
}

interface NFTComboBoxProps {
    items: IComboBoxItem[];
    onSelectedChange?: (item: IComboBoxItem) => void;
    style?: React.CSSProperties;
    defaultKey?: string;
    textPrefix?: string;
    allowSearch?: boolean;
}

const NFTComboBox = (props: NFTComboBoxProps) => {

    const [selectedItem, setSelectedItem] = React.useState<IComboBoxItem | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [searchParam, setSearchParam] = React.useState("");
    const containerRef = React.useRef<HTMLDivElement>(null);

    const onClickOutside = React.useCallback(() => {
        setSearchParam("");
        setIsOpen(false);
    }, []);

    useOnClickOutside(containerRef, onClickOutside);

    const onClick = React.useCallback((item: IComboBoxItem) => () => {
        setSelectedItem(item);
        setIsOpen(false);
        props.onSelectedChange && props.onSelectedChange(item);
    }, []);

    const renderSelectedItem = React.useCallback(() => {
        if (selectedItem) {
            if (selectedItem.renderItemText) {
                return <span>{selectedItem.renderItemText(selectedItem)}</span>;
            } else {
                return <span>{selectedItem.text}</span>
            }
        } else if (props.defaultKey) {
            const item = props.items.find(item => item.key === props.defaultKey);
            if (item) {
                if (item.renderItemText) {
                    return <span>{item.renderItemText(item)}</span>;
                } else {
                    return <span>{item.text}</span>
                }
            }
        }
        return <span>Select...</span>
    }, [selectedItem, props.defaultKey, props.items]);

    React.useEffect(() => {
        setSelectedItem(props.items.find(item => item.key === props.defaultKey) || null);
    }, [props.defaultKey])

    const onClickContainer = React.useCallback(() => {
        setIsOpen(!isOpen);
    }, [isOpen]);

    const searchFilter = React.useCallback((item: IComboBoxItem) => {
        if (searchParam) {
            return item.text.toLowerCase().includes(searchParam.toLowerCase());
        }
        return true;
    }, [searchParam]);

    return (
        <ComboBoxContainer ref={containerRef} style={props.style} className={cx({ open: isOpen })} onClick={onClickContainer}>
            <ComboBoxInner>
                <ComboBoxText>{props.textPrefix && <ComboBoxTextPrefix>{props.textPrefix}</ComboBoxTextPrefix>}{renderSelectedItem()}</ComboBoxText>
                <FontAwesomeIcon icon={faChevronDown} size={"1x"} className="comboboxArrow" />
            </ComboBoxInner>
            {props.items.length && <ComboBoxContent className="ComboBoxContent" onClick={(e) => { e.stopPropagation() }}>
                <Scrollbar autoHeight>
                    {props.allowSearch &&
                        <Input
                            placeholder={'Search'}
                            onChange={(value) => setSearchParam(value)}
                        />
                    }
                    {props.items.filter(searchFilter).map(item => {
                        if (item.renderItem) {
                            return (<div key={item.key} title={item.text} onClick={onClick(item)}>{item.renderItem(item)}</div>)
                        }
                        return (
                            <span key={item.key} title={item.text} onClick={onClick(item)}>{item.text}</span>
                        )
                    })
                    }
                </Scrollbar>
            </ComboBoxContent>}
        </ComboBoxContainer>
    )
}

export default NFTComboBox;